import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from '../styled';
import CatalogSearch from './CatalogSearch';
import CatalogChipRow from './CatalogChipRow';
import CatalogItemCard from './CatalogItemCard';
import CatalogEmpty from './CatalogEmpty';
import { sortBySearchRelevance } from './searchSort';
import { SkeletonBar, SkeletonSlot } from '../Skeleton';

const Wrap = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  flex: 1,
  minHeight: 0,
});

const Toolbar = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  flexShrink: 0,
});

const MetaRowBar = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: 12,
  color: 'var(--qhs-text-3)',
  padding: '4px 2px',
  flexShrink: 0,
});

const Pill = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '1px 8px',
  borderRadius: 999,
  background: 'var(--qhs-bg, #f5f7fa)',
  color: 'var(--qhs-text-2)',
  fontFamily: "'Source Code Pro', 'JetBrains Mono', monospace",
  fontWeight: 700,
  marginRight: 6,
});

// Room around the cards for what a card draws outside its box - the
// 1px hover lift, the shadow and the 3px focus ring - which the scroll
// clip cut off on the first card (its top edge sat flush with the
// scroller's). Negative side margins keep the cards aligned with the
// toolbar above.
const Scroll = styled('div')({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  margin: '0 -4px',
  padding: '4px 4px 12px',
});

// One card's outline while the catalog loads — CatalogItemCard's box:
// icon tile, title and subtitle, so the real cards land on it.
const CardSkeleton = styled('div')({
  display: 'grid',
  gridTemplateColumns: '36px 1fr',
  alignItems: 'center',
  gap: 12,
  padding: 14,
  border: '1px solid var(--qhs-border)',
  borderRadius: 10,
  background: 'var(--qhs-surface)',
});

const CardLines = styled('span')({ display: 'flex', flexDirection: 'column', gap: 4 });

const ChipSkeletons = styled('div')({ display: 'flex', gap: 6 });

const LOADING_CARDS = ['62%', '48%', '70%', '55%', '66%', '44%'];
const LOADING_CHIPS = [44, 86, 72, 64];

const HintFooter = styled('div')({
  fontSize: 12,
  color: 'var(--qhs-text-3)',
  padding: '6px 2px 0',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  '& svg': { width: 12, height: 12 },
});

const InfoIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const ALL = '__all__';
const OTHER = 'other';

const matches = (item, query) => {
  if (!query) return true;
  const q = query.toLowerCase();
  return (
    String(item.title ?? '')
      .toLowerCase()
      .includes(q) ||
    String(item.description ?? '')
      .toLowerCase()
      .includes(q)
  );
};

// Helper: does an item belong to a given category id?
//   - if `categoriesList` is provided, item.categories[] decides
//     membership (item with empty/missing categories falls into
//     the synthetic 'other' bucket if 'other' is in categoriesList).
//   - otherwise fall back to item.category (singular string) — old
//     behavior for callers that haven't migrated to the new shape.
const itemBelongsTo = (item, chipId, categoriesList, otherId) => {
  if (categoriesList) {
    const arr = Array.isArray(item.categories) ? item.categories : [];
    if (arr.includes(chipId)) return true;
    if (chipId === otherId) {
      const knownIds = new Set(categoriesList.map(c => c.id).filter(id => id !== otherId));
      return arr.length === 0 || !arr.some(id => knownIds.has(id));
    }
    return false;
  }
  return item.category === chipId;
};

const CatalogList = ({
  items,
  categoriesList,
  searchPlaceholder,
  itemsLabel,
  iconResolver,
  toneResolver,
  tagResolver,
  onSelect,
  autoFocus,
  hint,
  // The items are still loading: search, chips, count and list keep
  // their places with skeletons instead of claiming "0" and "nothing
  // found".
  loading = false,
}) => {
  const { catalog } = useSelector(state => state.i18n);
  const [query, setQuery] = useState('');
  const [activeChip, setActiveChip] = useState(ALL);

  const chips = useMemo(() => {
    if (categoriesList?.length) {
      const counts = new Map();
      (items ?? []).forEach(item => {
        categoriesList.forEach(cat => {
          if (itemBelongsTo(item, cat.id, categoriesList, OTHER)) {
            counts.set(cat.id, (counts.get(cat.id) ?? 0) + 1);
          }
        });
      });
      const definedChips = categoriesList
        .map(cat => ({
          id: cat.id,
          label: cat.label,
          count: counts.get(cat.id) ?? 0,
        }))
        // Hide categories with zero items (keeps Other only when needed).
        .filter(chip => chip.count > 0);
      return [{ id: ALL, label: catalog.allChip, count: items?.length ?? 0 }, ...definedChips];
    }
    // Fallback — derive from item.category strings (legacy behavior).
    const counts = new Map();
    (items ?? []).forEach(item => {
      const cat = item.category ?? null;
      if (!cat) return;
      counts.set(cat, (counts.get(cat) ?? 0) + 1);
    });
    return [
      { id: ALL, label: catalog.allChip, count: items?.length ?? 0 },
      ...Array.from(counts.entries()).map(([id, count]) => ({ id, label: id, count })),
    ];
  }, [items, categoriesList, catalog.allChip]);

  const filtered = useMemo(() => {
    return (items ?? [])
      .filter(item =>
        activeChip === ALL ? true : itemBelongsTo(item, activeChip, categoriesList, OTHER)
      )
      .filter(item => matches(item, query));
  }, [items, activeChip, query, categoriesList]);

  const metaText = (() => {
    if (query) {
      return catalog.matchesCountFor
        .replace('{{count}}', String(filtered.length))
        .replace('{{query}}', query);
    }
    if (activeChip !== ALL) {
      return catalog.itemsCount.replace('{{count}}', String(filtered.length));
    }
    return catalog.itemsCount.replace('{{count}}', String(items?.length ?? 0));
  })();

  return (
    <Wrap>
      <Toolbar>
        <SkeletonSlot active={loading}>
          <CatalogSearch
            value={query}
            onChange={setQuery}
            onClear={() => setQuery('')}
            placeholder={searchPlaceholder}
            autoFocus={autoFocus && !loading}
          />
        </SkeletonSlot>
        {loading && (
          <ChipSkeletons>
            {LOADING_CHIPS.map(width => (
              <SkeletonBar key={width} variant="rounded" width={width} height={28} radius={999} />
            ))}
          </ChipSkeletons>
        )}
        {!loading && chips.length > 1 && (
          <CatalogChipRow chips={chips} activeId={activeChip} onSelect={setActiveChip} />
        )}
      </Toolbar>
      {!loading && (
        <MetaRowBar>
          <span>
            {filtered.length > 0 && (query || activeChip !== ALL) && <Pill>{filtered.length}</Pill>}
            {metaText}
          </span>
          <span>{query ? catalog.bestMatchesFirst : catalog.sortedAlphabetically}</span>
        </MetaRowBar>
      )}
      {loading && <SkeletonBar variant="text" width={110} height={17} />}
      <Scroll>
        {loading &&
          LOADING_CARDS.map(width => (
            <CardSkeleton key={width} aria-hidden="true">
              <SkeletonBar variant="rounded" width={36} height={36} radius={8} />
              <CardLines>
                <SkeletonBar variant="text" width={width} height={18} />
                <SkeletonBar variant="text" width="35%" height={15} />
              </CardLines>
            </CardSkeleton>
          ))}
        {!loading && filtered.length === 0 && (
          <CatalogEmpty query={query} itemsLabel={itemsLabel} />
        )}
        {!loading && filtered.length > 0 && (
          sortBySearchRelevance(filtered, query).map(item => (
            <CatalogItemCard
              key={item.id ?? item.resourceKey ?? item.title}
              item={item}
              query={query}
              tone={toneResolver?.(item)}
              icon={iconResolver?.(item)}
              tag={tagResolver?.(item)}
              onClick={onSelect}
            />
          ))
        )}
      </Scroll>
      {hint && (
        <HintFooter>
          <InfoIcon />
          {hint}
        </HintFooter>
      )}
    </Wrap>
  );
};

export default CatalogList;
